import React from 'react';
import { Download, Mail, FileText, Copy } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import { formatMoney, formatDate } from '../../../utils/formatters';

const FacturaDetailModal = ({
  factura,
  catalogos,
  onClose,
  onDescargarXML,
  onDescargarPDF,
  onReenviar
}) => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getEstadoClass = (estado) => {
    switch (estado) {
      case 'vigente': return 'vigente';
      case 'cancelada': return 'cancelada';
      case 'pendiente_timbrado': return 'pendiente_timbrado';
      default: return '';
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'vigente': return 'Vigente';
      case 'cancelada': return 'Cancelada';
      case 'pendiente_timbrado': return 'Pendiente de Timbrado';
      default: return estado;
    }
  };

  return (
    <Modal
      title="Detalle de Factura"
      onClose={onClose}
      size="xlarge"
    >
      <div className="factura-detail">
        {/* Header */}
        <div className="factura-detail-header">
          <div>
            <h2 className="factura-folio">{factura.folio}</h2>
            {factura.uuid && (
              <p className="factura-uuid">
                UUID: {factura.uuid}
                <button
                  style={{
                    marginLeft: '0.5rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#667eea'
                  }}
                  onClick={() => copyToClipboard(factura.uuid)}
                  title="Copiar UUID"
                >
                  <Copy size={14} />
                </button>
              </p>
            )}
          </div>
          <span className={`estado-badge ${getEstadoClass(factura.estado)}`}>
            {getEstadoLabel(factura.estado)}
          </span>
        </div>

        {/* Datos emisor y receptor */}
        <div className="factura-detail-grid">
          <div className="detail-section">
            <h4>Emisor</h4>
            <div className="detail-row">
              <label>RFC:</label>
              <span>{factura.emisor?.rfc}</span>
            </div>
            <div className="detail-row">
              <label>Razón Social:</label>
              <span>{factura.emisor?.razon_social}</span>
            </div>
            <div className="detail-row">
              <label>Régimen Fiscal:</label>
              <span>
                {catalogos.regimenesFiscales.find(r => r.clave === factura.emisor?.regimen_fiscal)?.descripcion || factura.emisor?.regimen_fiscal}
              </span>
            </div>
          </div>

          <div className="detail-section">
            <h4>Receptor</h4>
            <div className="detail-row">
              <label>RFC:</label>
              <span>{factura.receptor?.rfc}</span>
            </div>
            <div className="detail-row">
              <label>Razón Social:</label>
              <span>{factura.receptor?.razon_social}</span>
            </div>
            <div className="detail-row">
              <label>Régimen Fiscal:</label>
              <span>
                {catalogos.regimenesFiscales.find(r => r.clave === factura.receptor?.regimen_fiscal)?.descripcion || factura.receptor?.regimen_fiscal}
              </span>
            </div>
            <div className="detail-row">
              <label>Uso CFDI:</label>
              <span>
                {catalogos.usosCFDI.find(u => u.clave === factura.receptor?.uso_cfdi)?.descripcion || factura.receptor?.uso_cfdi}
              </span>
            </div>
          </div>
        </div>

        {/* Datos de pago */}
        <div className="factura-detail-grid" style={{ marginTop: '1rem' }}>
          <div className="detail-section">
            <h4>Datos de Pago</h4>
            <div className="detail-row">
              <label>Forma de Pago:</label>
              <span>
                {catalogos.formasPago.find(f => f.clave === factura.forma_pago)?.descripcion || factura.forma_pago}
              </span>
            </div>
            <div className="detail-row">
              <label>Método de Pago:</label>
              <span>
                {catalogos.metodosPago.find(m => m.clave === factura.metodo_pago)?.descripcion || factura.metodo_pago}
              </span>
            </div>
            <div className="detail-row">
              <label>Moneda:</label>
              <span>{factura.moneda}</span>
            </div>
          </div>

          <div className="detail-section">
            <h4>Fechas</h4>
            <div className="detail-row">
              <label>Emisión:</label>
              <span>{formatDate(factura.fecha_emision)}</span>
            </div>
            {factura.fecha_timbrado && (
              <div className="detail-row">
                <label>Timbrado:</label>
                <span>{formatDate(factura.fecha_timbrado)}</span>
              </div>
            )}
            {factura.fecha_cancelacion && (
              <div className="detail-row">
                <label>Cancelación:</label>
                <span>{formatDate(factura.fecha_cancelacion)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Conceptos */}
        {factura.conceptos && factura.conceptos.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <h4 style={{ marginBottom: '0.75rem', color: '#374151' }}>Conceptos</h4>
            <table className="conceptos-table">
              <thead>
                <tr>
                  <th>Cant.</th>
                  <th>Descripción</th>
                  <th>P. Unitario</th>
                  <th>Importe</th>
                  <th>IVA</th>
                </tr>
              </thead>
              <tbody>
                {factura.conceptos.map((concepto, index) => (
                  <tr key={index}>
                    <td>{concepto.cantidad}</td>
                    <td>{concepto.descripcion}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoney(concepto.valor_unitario)}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoney(concepto.importe)}</td>
                    <td style={{ textAlign: 'right' }}>{formatMoney(concepto.importe_iva)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Ventas relacionadas */}
        {factura.ventas && factura.ventas.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <h4 style={{ marginBottom: '0.75rem', color: '#374151' }}>Ventas Incluidas</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {factura.ventas.map(venta => (
                <span
                  key={venta.id}
                  style={{
                    padding: '0.25rem 0.75rem',
                    background: '#e0e7ff',
                    color: '#4338ca',
                    borderRadius: '0.25rem',
                    fontSize: '0.875rem'
                  }}
                >
                  #{venta.folio} - {formatMoney(venta.total)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Totales */}
        <div className="factura-totales" style={{ marginTop: '1.5rem' }}>
          <div className="total-row">
            <label>Subtotal:</label>
            <span>{formatMoney(factura.subtotal)}</span>
          </div>
          {factura.descuento > 0 && (
            <div className="total-row">
              <label>Descuento:</label>
              <span>-{formatMoney(factura.descuento)}</span>
            </div>
          )}
          <div className="total-row">
            <label>IVA (16%):</label>
            <span>{formatMoney(factura.total_impuestos)}</span>
          </div>
          <div className="total-row final">
            <label>TOTAL:</label>
            <span>{formatMoney(factura.total)}</span>
          </div>
        </div>

        {/* Acciones */}
        <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
          <button className="btn-cancelar" onClick={onClose}>
            Cerrar
          </button>

          {factura.estado !== 'pendiente_timbrado' && (
            <>
              <button
                className="btn-accion"
                onClick={onDescargarXML}
                style={{ padding: '0.625rem 1rem', border: '1px solid #d1d5db' }}
              >
                <FileText size={16} />
                XML
              </button>
              <button
                className="btn-accion"
                onClick={onDescargarPDF}
                style={{ padding: '0.625rem 1rem', border: '1px solid #d1d5db' }}
              >
                <Download size={16} />
                PDF
              </button>
              <button
                className="btn-guardar"
                onClick={onReenviar}
              >
                <Mail size={16} />
                Enviar por Email
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default FacturaDetailModal;
